import React, { useMemo, useEffect, useCallback } from 'react';
import { Form, Popconfirm, notification } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import { useQuery } from 'react-query';
import DF from 'DisplayFramework';
import { indexBy, setValueByNamePath } from 'utils/utils2';
import Button from 'components/AddButtonSubEntity';
import SaveButton from 'components/SaveButtonAction';
import DataGrid from 'components/DataGrid/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import {
  ParAssetCatalogServiceResponse,
  getAssetCatalogServiceList,
  addAssetCatalogService,
  updateAssetCatalogService,
  removeAssetCatalogService,
  AssetCatalogResponse,
} from 'services/assetconfig';

import { useFormList } from 'hooks/useFormList';
import DeleteButton from 'components/DeleteButton';
import { isNil } from 'lodash';

import InputRate from 'components/InputRate';
// import InputMoney from 'components/InputMoney';
import ServiceList from 'components/ServiceList';
import InputCurrency from 'components/InputCurrency';

const NEW_CATALOG_SERVICE_ID = -1;

type AssetCatalogServicesTableProps = {
  asset: AssetCatalogResponse;
  isModal?: string;
};

const AssetCatalogServicesTable = (props: AssetCatalogServicesTableProps) => {
  const { catalogid,currcode } = props.asset;
  const { isModal } = props;
  const [form] = Form.useForm();
  const fieldName = 'services';

  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'associd',
    addAsFirst: true,
  });

  const { data, refetch } = useQuery(
    ['getAssetCatalogServiceList', catalogid],
    () =>
      getAssetCatalogServiceList({ catalogid }).then((res) => res.list || []),
    { refetchOnMount: false, enabled: catalogid !== undefined }
  );

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        [fieldName]: data,
      });
    } else {
      form.setFieldsValue({
        [fieldName]: [],
      });
    }
  }, [form, data]);

  const addRow = () => {
    const row: ParAssetCatalogServiceResponse = {
      associd: NEW_CATALOG_SERVICE_ID,
      catalogid,
      servcode: '',
      servfixedamount: 0,
      servfixedamountdefault: 0,
      servlabel: '',
      servpercentage: 0,
      servpercentagedefault: 0,
      servdescription:'',
      servusecoverage:0,
      servusedeductionamount:0,
      servusedeductionpercenrage:0,
      servusefrequency:'',
      servusefrequencylabel:'',
      servusemaxamount:0,
      servuseminamount:0,
      servuseqty:0,
      servuseunit:'',
      servuseunitlabel:'',
      cteid:0,
      enddate:'',
      startdate:'',
      status:'',
    };
    add(row);
  };

  const onSave = useCallback(
    (record: any) => {
      const steps: any[] = form.getFieldValue(fieldName);
      const index = indexBy(steps, 'associd')[record.associd];
      const currentValue = steps[index];
      const { associd } = currentValue;
      associd === NEW_CATALOG_SERVICE_ID
        ? addAssetCatalogService({
            catalogid,
            req: {
              ...currentValue,
              servcode: currentValue.servcode,
              servlabel: currentValue.servlabel,
            },
          }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message,
              });
              refetch();
            }
          })
        : updateAssetCatalogService({
            catalogid,
            req: {
              ...currentValue,
              servcode:
                currentValue.servcode.serviceResponse?.servcode ??
                currentValue.servcode,
            },
          }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message,
              });
              refetch();
            }
          });
    },
    [catalogid, form, refetch]
  );

  const columns = useMemo<DataGridColDef<ParAssetCatalogServiceResponse>[]>(
    () => [
      // {
      //   dataIndex: 'associd',
      //   width: 100,
      //   render: (_value, _record, index) => (
      //     <Form.Item
      //       name={[
      //         fieldName,
      //         indexBy(form.getFieldValue(fieldName), 'associd')[
      //           _record.associd
      //         ],
      //         'associd',
      //       ]}
      //     >
      //       {_value}
      //     </Form.Item>
      //   ),
      // },
      {
        dataIndex: 'servlabel',
        render: (_value, _record, index) =>
          _record.associd === -1 ? (
            <Form.Item
              name={[
                fieldName,
                indexBy(form.getFieldValue(fieldName), 'associd')[
                  _record.associd
                ],
                'servcode',
              ]}
              noStyle
            >
              <ServiceList
                style={{ width: '100%' }}
                servtype="SERV"
                onSelected={(e) => {
                  setValueByNamePath(
                    form,
                    [
                      fieldName,
                      indexBy(form.getFieldValue(fieldName), 'associd')[
                        _record.associd
                      ],
                      'servpercentage',
                    ],
                    e?.servrepaypercentage ?? 0
                  );

                  setValueByNamePath(
                    form,
                    [
                      fieldName,
                      indexBy(form.getFieldValue(fieldName), 'associd')[
                        _record.associd
                      ],
                      'servfixedamount',
                    ],
                    e?.servfixedamount ?? 0
                  );
                }}
              />
            </Form.Item>
          ) : (
            _record?.servlabel
          ),
        width: 150,
      },
      // {
      //   dataIndex: 'servlabel',
      //   render: (_value, _record, index) => (
      //     <Form.Item
      //       name={[
      //         fieldName,
      //         indexBy(form.getFieldValue(fieldName), 'associd')[
      //           _record.associd
      //         ],
      //         'servlabel',
      //       ]}
      //     >
      //       <Input  />
      //     </Form.Item>
      //   ),
      //   width: 200,
      // },
      {
        dataIndex: 'servpercentage',
        render: (_value, _record, index) => {
          return (
            <Form.Item
              name={[
                fieldName,
                indexBy(form.getFieldValue(fieldName), 'associd')[
                  _record.associd
                ],
                'servpercentage',
              ]}
              noStyle
            >
              <InputRate style={{ width: '100%' }} precision={2} />
            </Form.Item>
          );
        },
        width: 150,
      },
      {
        dataIndex: 'servfixedamount',
        render: (_value, _record, index) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'associd')[
                _record.associd
              ],
              'servfixedamount',
            ]}
            noStyle
          >
            <InputCurrency
              style={{ width: '100%' }}
              currcode={currcode}
            />
          </Form.Item>
        ),
        width: 180,
      },
      // {
      //   dataIndex: 'servpercentagedefault',
      //   render: (_value, _record, index) => (
      //     <Form.Item
      //       name={[
      //         fieldName,
      //         indexBy(form.getFieldValue(fieldName), 'associd')[
      //           _record.associd
      //         ],
      //         'servpercentagedefault',
      //       ]}
      //     >
      //       <InputRate precision={2} />
      //     </Form.Item>
      //   ),
      //   width: 200,
      // },
      // {
      //   dataIndex: 'servfixedamountdefault',
      //   render: (_value, _record, index) => (
      //     <Form.Item
      //       name={[
      //         fieldName,
      //         indexBy(form.getFieldValue(fieldName), 'associd')[
      //           _record.associd
      //         ],
      //         'servfixedamountdefault',
      //       ]}
      //     >
      //       <InputMoney style={{ width: '100%' }} />
      //     </Form.Item>
      //   ),
      //   width: 200,
      // },
      {
        key: 'actions',
        render: (_, _record) =>
          _record.associd === NEW_CATALOG_SERVICE_ID ? (
            <>
              <SaveButton
                onClick={(ev) => {
                  ev.preventDefault();
                  onSave(_record);
                }}
                size="small"
                comcode="save"
              />
              <DeleteButton onlyIcon onClick={() => remove(_record.associd)} />
            </>
          ) : (
            <>
              <SaveButton
                onClick={(ev) => {
                  ev.preventDefault();
                  onSave(_record);
                }}
                size="small"
                comcode="update"
              />
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => {
                  removeAssetCatalogService({
                    associd: _record.associd,
                    catalogid: _record.catalogid,
                  }).then((res) => {
                    notification.success({ message: res.message });
                    refetch();
                  });
                }}
              >
                <DeleteButton onlyIcon />
              </Popconfirm>
            </>
          ),
        width: 180,
        fixed: 'right',
      },
    ],
    [form, onSave, refetch, remove]
  );

  return (
    <DF.Provider pagcode="assetCatalogServices">
      <Form form={form} initialValues={INITIAL_VALUES}>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const services: ParAssetCatalogServiceResponse[] =
              getFieldValue(fieldName);

            const isNewItemInTable =
              services?.find(
                ({ associd }) => associd === NEW_CATALOG_SERVICE_ID
              ) !== undefined;

            return (
              <DataGrid
                rowKey="associd"
                columns={columns}
                dataSource={services}
                titleComcode="assetCatalogServices"
                hideFilters
                extraActions={
                  <Button
                    type="primary"
                    disabled={isNewItemInTable}
                    icon={<PlusOutlined />}
                    onClick={addRow}
                    comcode="add"
                    hidden={isNil(catalogid) || !!isModal}
                  />
                }
              />
            );
          }}
        </Form.Item>
      </Form>
    </DF.Provider>
  );
};

const INITIAL_VALUES = {
  services: [],
};

export default AssetCatalogServicesTable;
